/**
 * 品牌主色工具：由单个 #rrggbb 派生 50~950 完整色板（写入 --brand-* CSS 变量），
 * 并提供设置页用的预设色列表。
 *
 * 派生方式：500 为基准色，浅色档（50~400）按比例与白混合，深色档（600~950）
 * 按比例与黑混合。比例参照 Tailwind 蓝色板的相对明度，视觉上与手写色板接近。
 */

export interface Rgb {
  r: number
  g: number
  b: number
}

export function parseHexColor(hex: string): Rgb | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  }
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 }
const BLACK: Rgb = { r: 0, g: 0, b: 0 }

/** 各档位与基准色的混合比例（向白 / 向黑）。 */
const LIGHT_MIX: Record<string, number> = {
  50: 0.95,
  100: 0.88,
  200: 0.76,
  300: 0.6,
  400: 0.38,
}
const DARK_MIX: Record<string, number> = {
  600: 0.14,
  700: 0.28,
  800: 0.42,
  900: 0.56,
  950: 0.72,
}

export const BRAND_SCALE_STOPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const

/**
 * 由基准 hex 生成完整色板。返回值 key 为档位、value 为 Tailwind CSS 变量需要的
 * 空格分隔 RGB 通道串（如 "59 130 246"）。非法 hex 返回 null。
 */
export function buildBrandScale(hex: string): Record<string, string> | null {
  const base = parseHexColor(hex)
  if (!base) return null
  const toVar = (c: Rgb) => `${c.r} ${c.g} ${c.b}`
  const scale: Record<string, string> = { 500: toVar(base) }
  for (const [stop, t] of Object.entries(LIGHT_MIX)) scale[stop] = toVar(mix(base, WHITE, t))
  for (const [stop, t] of Object.entries(DARK_MIX)) scale[stop] = toVar(mix(base, BLACK, t))
  return scale
}

/** 设置页「品牌主色」预设色板。value 即 500 档基准色。 */
export const BRAND_COLOR_PRESETS: Array<{ name: string; value: string }> = [
  { name: '默认蓝', value: '#3b82f6' },
  { name: '靛蓝', value: '#6366f1' },
  { name: '紫', value: '#8b5cf6' },
  { name: '青', value: '#06b6d4' },
  { name: '翠绿', value: '#10b981' },
  { name: '琥珀', value: '#f59e0b' },
  { name: '玫红', value: '#f43f5e' },
  { name: '石墨', value: '#64748b' },
]
